// frontend/klaes-app/src/components/FilmFavs/components/TagPicker.jsx
import { useState } from 'react';
import { FiX, FiCheck, FiPlus, FiTrash2 } from 'react-icons/fi';
import { Chip } from './Chip';
import { tagCategories, categoryColors } from '../utils/helpers';
import { styles } from '../styles';

export const TagPicker = ({ 
  onSelectTag,
  onClose, 
  existingTags, 
  onCreateTag, 
  onDeleteTag,
  selectedTags = []
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newTagName, setNewTagName] = useState('');
  const [showCreateTag, setShowCreateTag] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  const filteredTags = existingTags.filter(tag => {
    const matchesCategory = selectedCategory === 'all' || tag.category === selectedCategory;
    const matchesSearch = searchTerm === '' || tag.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const tagExists = existingTags.some(tag => 
    tag.name.toLowerCase() === searchTerm.toLowerCase()
  );

  const handleCreateTag = () => {
    if (newTagName.trim() && selectedCategory !== 'all') {
      onCreateTag({
        name: newTagName.trim(),
        category: selectedCategory,
        colour: categoryColors[selectedCategory] || '#6366f1'
      });
      setNewTagName('');
      setShowCreateTag(false);
      setSearchTerm('');
    }
  };

  const handleStartCreate = () => {
    if (searchTerm && !tagExists && selectedCategory !== 'all') {
      setNewTagName(searchTerm);
      setShowCreateTag(true);
    }
  };

  const groupedTags = filteredTags.reduce((acc, tag) => {
    if (!acc[tag.category]) acc[tag.category] = [];
    acc[tag.category].push(tag);
    return acc;
  }, {});

  const isSelected = (tag) => selectedTags.some(t => t.name === tag.name);

  const handleTagClick = (tag) => {
    onSelectTag(tag);
  };

  const handleDeleteClick = (tag) => {
    setDeleteCandidate(tag); 
  };

  const confirmDelete = () => {
    if (deleteCandidate) {
      onDeleteTag(deleteCandidate);
      setDeleteCandidate(null);
    }
  };

  const handleConfirmClose = () => {
    onClose();
  };

  return (
    <div className={styles.modalTagPickerOverlay} onClick={onClose}>
      <div
        className={styles.modalTagPicker}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalTagPickerHeader}>
          <h4>Tags auswählen / erstellen</h4>
          <div className={styles.modalHeaderButtons}>
            <button
              onClick={handleConfirmClose}
              className={styles.modalConfirmPickerBtn}
              title="Übernehmen"
            >
              <FiCheck />
            </button>
            <button onClick={onClose} className={styles.modalClosePickerBtn}>
              <FiX />
            </button>
          </div>
        </div>

        <div className={styles.modalTagPickerSearch}>
          <input
            type="text"
            placeholder="Tag suchen oder neuen erstellen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleStartCreate()}
            className={styles.modalTagPickerInput}
          />
        </div>

        <div className={styles.modalTagPickerCategories}>
          {tagCategories.map((cat) => (
            <button
              key={cat.id}
              className={`${styles.modalCategoryBtn} ${selectedCategory === cat.id ? styles.modalActive : ""}`}
              onClick={() => setSelectedCategory(cat.id)}
            >
              <span>{cat.icon}</span> {cat.label}
            </button>
          ))}
        </div>

        <div className={styles.modalTagPickerList}>
          {showCreateTag && (
            <div className={styles.modalCreateTagModal}>
              <p>Neuen Tag erstellen:</p>
              <div className={styles.modalCreateTagForm}>
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Tag Name"
                  autoFocus
                />
                <div className={styles.modalCreateTagCategory}>
                  <span>
                    Kategorie:{" "}
                    {
                      tagCategories.find((c) => c.id === selectedCategory)
                        ?.label
                    }
                  </span>
                </div>
                <div className={styles.modalCreateTagButtons}>
                  <button
                    onClick={handleCreateTag}
                    className={styles.modalConfirmCreateBtn}
                  >
                    Erstellen
                  </button>
                  <button
                    onClick={() => setShowCreateTag(false)}
                    className={styles.modalCancelCreateBtn}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          )}

          {selectedCategory === "all" ? (
            Object.entries(groupedTags).map(([category, tags]) => (
              <div key={category} className={styles.modalTagCategoryGroup}>
                <div className={styles.modalTagCategoryLabel}>
                  {tagCategories.find((c) => c.id === category)?.icon}{" "}
                  {tagCategories.find((c) => c.id === category)?.label ||
                    category}
                </div>
                <div className={styles.modalTagPickerTags}>
                  {tags.map((tag) => (
                    <Chip
                      key={tag.name}
                      label={tag.name}
                      category={tag.category}
                      isSelected={isSelected(tag)}
                      onClick={() => handleTagClick(tag)}
                      onDelete={() => handleDeleteClick(tag)}
                      size="medium"
                    />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className={styles.modalTagPickerTags}>
              {(groupedTags[selectedCategory] || []).map((tag) => (
                <Chip
                  key={tag.name}
                  label={tag.name}
                  category={tag.category}
                  isSelected={isSelected(tag)}
                  onClick={() => handleTagClick(tag)}
                  onDelete={() => handleDeleteClick(tag)}
                  size="medium"
                />
              ))}
              {!showCreateTag && searchTerm && !tagExists && (
                <button
                  onClick={handleStartCreate}
                  className={styles.modalCreateNewTagHint}
                >
                  <FiPlus /> "{searchTerm}" als neuen Tag erstellen
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {/* NEU: Lösch-Bestätigung */}
      {deleteCandidate && (
        <div
          className={styles.modalOverlay}
          onClick={(e) => {
            e.stopPropagation();
            setDeleteCandidate(null);
          }}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Tag löschen</h3>
            <p style={{ marginBottom: "1rem" }}>
              <strong>"{deleteCandidate.name}"</strong> wirklich löschen?
              <br />
              Der Tag wird aus allen Einträgen entfernt.
            </p>
            <div className={styles.modalButtons}>
              <button
                onClick={confirmDelete}
                className={styles.modalDeleteConfirmBtn}
              >
                <FiTrash2 /> Ja, löschen
              </button>
              <button
                onClick={() => setDeleteCandidate(null)}
                className={styles.modalDeleteCancelBtn}
              >
                <FiX /> Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};    
